/* Grădina de Lumină — native Shopify theme, no external JavaScript dependencies. */
(() => {
  'use strict';
  class GardenCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const on = (target, type, handler) => target.addEventListener(type, handler, { signal: this.abort.signal });
      this.slides = [...this.querySelectorAll('[data-carousel-slide]')];
      if (!this.slides.length) return;
      this.current = 0;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches;
      this.hovered = false;
      this.focused = false;
      this.editorSelected = false;
      this.dots = [];
      const dots = this.querySelector('[data-carousel-dots]');
      dots.replaceChildren();
      this.slides.forEach((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button'; dot.className = 'gl-dot';
        dot.setAttribute('aria-label', `${index + 1}: ${slide.dataset.label}`);
        on(dot, 'click', () => this.go(index, true));
        dots.append(dot); this.dots.push(dot);
        on(slide, 'click', () => { if (!this.suppressClick) this.go(index, true); });
      });
      this.querySelector('[data-carousel-controls]').hidden = this.slides.length < 2;
      this.pauseButton = this.querySelector('[data-carousel-pause]');
      on(this.querySelector('[data-carousel-prev]'), 'click', () => this.go(this.current - 1, true));
      on(this.querySelector('[data-carousel-next]'), 'click', () => this.go(this.current + 1, true));
      on(this.pauseButton, 'click', () => { this.paused = !this.paused; this.updatePause(); this.schedule(); });
      on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } });
      on(this, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      on(this, 'focusin', () => { this.focused = true; this.schedule(); });
      on(this, 'focusout', event => { this.focused = this.contains(event.relatedTarget); this.schedule(); });
      on(this, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.go(this.current + (event.key === 'ArrowLeft' ? -1 : 1), true);
      });
      const scene = this.querySelector('[data-carousel-scene]');
      on(scene, 'pointerdown', event => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.start = { x: event.clientX, y: event.clientY };
      });
      on(scene, 'pointerup', event => {
        if (!this.start) return;
        const dx = event.clientX - this.start.x, dy = event.clientY - this.start.y;
        this.start = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this.suppressClick = true; this.go(this.current + (dx > 0 ? -1 : 1), true);
          clearTimeout(this.clickTimer); this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 250);
        }
      });
      on(scene, 'pointercancel', () => { this.start = null; });
      on(document, 'visibilitychange', () => this.schedule());
      on(this.motion, 'change', () => { if (this.motion.matches) this.paused = true; this.updatePause(); this.schedule(); });
      on(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.editorSelected = true; this.go(index, true);
      });
      on(document, 'shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); });
      this.dataset.initialized = 'true';
      this.go(0, false); this.updatePause();
    }
    disconnectedCallback() {
      clearTimeout(this.timer); clearTimeout(this.clickTimer);
      this.abort?.abort(); this.abort = null;
    }
    go(index, announce) {
      const count = this.slides.length;
      this.current = ((index % count) + count) % count;
      this.slides.forEach((slide, i) => {
        const relative = (i - this.current + count) % count;
        let position = 'hidden';
        if (relative === 0) position = 'center';
        else if (relative === 1) position = 'right';
        else if (relative === count - 1) position = 'left';
        slide.dataset.position = position;
        slide.tabIndex = position === 'hidden' ? -1 : 0;
        slide.setAttribute('aria-hidden', String(position === 'hidden'));
        slide.setAttribute('aria-pressed', String(i === this.current));
        this.dots[i].setAttribute('aria-pressed', String(i === this.current));
      });
      const active = this.slides[this.current];
      const link = this.querySelector('[data-carousel-link]');
      link.hidden = !active.dataset.link;
      if (active.dataset.link) link.setAttribute('href', active.dataset.link); else link.removeAttribute('href');
      if (announce) this.querySelector('[data-carousel-status]').textContent = `${this.current + 1} / ${count}: ${active.dataset.label}`;
      this.schedule();
    }
    updatePause() {
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton.setAttribute('aria-label', this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
      this.pauseButton.querySelector('[data-carousel-pause-icon]').textContent = this.paused ? '▶' : 'Ⅱ';
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.paused || this.hovered || this.focused || this.editorSelected || document.hidden || this.slides.length < 2) return;
      this.timer = setTimeout(() => this.go(this.current + 1, false), Math.max(3000, Number(this.dataset.interval) || 5000));
    }
  }
  customElements.define('gl-carousel', GardenCarousel);
  function initializeProducts(root = document) {
    root.querySelectorAll('[data-product-section]').forEach(section => {
      const select = section.querySelector('[data-variant-select]');
      if (!select || select.dataset.initialized) return;
      select.dataset.initialized = 'true';
      select.addEventListener('change', () => {
        const variant = select.selectedOptions[0];
        if (!variant) return;
        const price = section.querySelector('[data-product-price]');
        const wrapper = document.createElement('span'); wrapper.className = 'gl-price';
        const currentPrice = document.createElement('span'); currentPrice.textContent = variant.dataset.price;
        wrapper.append(currentPrice);
        if (variant.dataset.compare) { const compare = document.createElement('s'); compare.textContent = variant.dataset.compare; wrapper.append(compare); }
        price.replaceChildren(wrapper);
        const quantity = section.querySelector('[data-product-quantity]');
        quantity.min = variant.dataset.min || '1'; quantity.step = variant.dataset.step || '1';
        if (variant.dataset.max) quantity.max = variant.dataset.max; else quantity.removeAttribute('max');
        quantity.value = quantity.min;
        const button = section.querySelector('[data-add-button]');
        button.disabled = variant.dataset.available !== 'true';
        button.textContent = button.disabled ? button.dataset.soldLabel : button.dataset.addLabel;
        const checkout = section.querySelector('[data-dynamic-checkout]');
        if (checkout) checkout.hidden = button.disabled;
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.value); history.replaceState({}, '', url);
        if (variant.dataset.media) {
          const media = section.querySelector(`[data-media-id="${CSS.escape(variant.dataset.media)}"]`);
          if (media && window.matchMedia('(min-width: 761px)').matches) media.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      });
    });
  }
  function initializeAddresses(root = document) {
    root.querySelectorAll('[data-country]').forEach(country => {
      if (country.dataset.initialized) return;
      country.dataset.initialized = 'true';
      const province = document.getElementById(country.dataset.province);
      const update = () => {
        const values = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]');
        province.replaceChildren(...values.map(([value, label]) => new Option(label, value)));
        if (values.some(([value]) => value === province.dataset.default)) province.value = province.dataset.default;
        province.closest('[data-province-wrap]').hidden = values.length === 0;
        province.disabled = values.length === 0;
      };
      if ([...country.options].some(option => option.value === country.dataset.default)) country.value = country.dataset.default;
      update(); country.addEventListener('change', update);
    });
  }
  initializeProducts(); initializeAddresses();
  document.querySelectorAll('details .errors, details [role="status"]').forEach(message => { if (message.textContent.trim()) message.closest('details').open = true; });
  document.addEventListener('shopify:section:load', event => { initializeProducts(event.target); initializeAddresses(event.target); });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.gl-mobile-menu[open], .gl-nav-dropdown[open], .gl-filter[open]').forEach(details => { details.open = false; details.querySelector('summary')?.focus(); });
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('.gl-nav-dropdown[open], .gl-filter[open], .gl-mobile-menu[open]').forEach(details => { if (!details.contains(event.target)) details.open = false; });
  });
})();
